/**
 * Helpers for the poster-ads screen (benefits carousel of the app).
 */

const OPTIONAL_TEXT_FIELDS = [
  'subtitle',
  'description',
  'internalDescription',
  'webUrl',
  'appRoute',
  'deskImage',
  'mobileImage',
  'backgroundColor'
];

/** Blank form for a new poster-ad, with the same defaults as the `poster-ads` table. */
export const emptyPosterAdForm = () => ({
  title: '',
  subtitle: '',
  description: '',
  internalDescription: '',
  order: null,
  visible: false,
  available: true,
  isb2b: false,
  startDate: null,
  endDate: null,
  webUrl: '',
  appRoute: '',
  deskImage: '',
  mobileImage: '',
  backgroundColor: '',
  allianceId: null,
  countryId: null
});

const toDateInput = (value) => (value ? String(value).substring(0, 10) : null);

/**
 * Maps a poster-ad from the API into the form shape.
 * @param {object} posterAd
 */
export const toPosterAdForm = (posterAd) => {
  const form = emptyPosterAdForm();
  Object.keys(form).forEach((key) => {
    if (posterAd[key] !== undefined && posterAd[key] !== null) form[key] = posterAd[key];
  });
  form.startDate = toDateInput(posterAd.start_date);
  form.endDate = toDateInput(posterAd.end_date);
  return form;
};

/**
 * Builds the body for POST/PUT /posterads. Empty optional strings become null and an
 * empty contract list becomes null, which means "visible for everyone in the country".
 * @param {object} form
 * @param {string[]} contractIds
 */
export const buildPosterAdPayload = (form, contractIds) => {
  const payload = {
    title: form.title.trim(),
    order: Number(form.order),
    visible: !!form.visible,
    available: !!form.available,
    isb2b: !!form.isb2b,
    start_date: form.startDate || null,
    end_date: form.endDate || null,
    allianceId: form.allianceId || null,
    countryId: form.countryId || null,
    contratoId: contractIds.length ? [...contractIds] : null
  };
  OPTIONAL_TEXT_FIELDS.forEach((key) => {
    const value = typeof form[key] === 'string' ? form[key].trim() : form[key];
    payload[key] = value || null;
  });
  return payload;
};

/**
 * Human-readable contract label, same format as the contract selector of AssociateCompanyModal.
 * @param {{ codigo?: string, plan?: { name?: string }, status?: string }} contract
 */
export const contractLabel = (contract) => {
  const planName = contract.plan && contract.plan.name ? ` · ${contract.plan.name}` : '';
  const status = contract.status ? ` (${contract.status})` : '';
  return `${contract.codigo || 'Contrato'}${planName}${status}`;
};

/**
 * Entry used for the contract chips: id, company name and label.
 * @param {object} contract - contract as returned by /contratos (with `company`).
 * @param {string} [fallbackCompanyName]
 */
export const toContractOption = (contract, fallbackCompanyName = '') => ({
  id: contract.id,
  companyName: (contract.company && contract.company.name) || fallbackCompanyName,
  label: contractLabel(contract)
});

/**
 * Text of the "Alcance" column: "Todos" when the ad has no contracts.
 * @param {{ contratoId?: string[]|null }} posterAd
 */
export const scopeLabel = (posterAd) => {
  const count = Array.isArray(posterAd.contratoId) ? posterAd.contratoId.length : 0;
  if (count === 0) return 'Todos';
  return count === 1 ? '1 contrato' : `${count} contratos`;
};
